/**
 * Configuration centralisee des URLs API
 * Permet de gerer les environnements et eviter les URLs hardcodees
 *
 * Migration v2.0: sessions -> prestations, rooms -> groups, trainers -> team_leaders
 *
 * Note: En production (Docker), les variables sont injectees via window._env_
 * En developpement, on utilise process.env.REACT_APP_* ou les valeurs par defaut
 */

// Helper pour recuperer une variable d'environnement (runtime ou build-time)
const getEnvVar = (name, defaultValue) => {
  // Priorite 1: Variables runtime (Docker)
  if (typeof window !== 'undefined' && window._env_ && window._env_[name]) {
    return window._env_[name];
  }
  // Priorite 2: Variables build-time (developpement)
  if (process.env[name]) {
    return process.env[name];
  }
  // Priorite 3: Valeur par defaut
  return defaultValue;
};

// Configuration base selon l'environnement
export const API_BASE_URL = getEnvVar('REACT_APP_API_URL', 'http://localhost:3001');

// Configuration des endpoints par domaine
export const API_ENDPOINTS = {
  // Base URL pour construire des URLs personnalisées
  BASE_URL: API_BASE_URL,

  // Prestations (anciennement Sessions)
  PRESTATIONS: `${API_BASE_URL}/api/prestations`,
  MY_PLANNING: `${API_BASE_URL}/api/prestations/my-planning`,
  PRESTATION_BY_ID: (id) => `${API_BASE_URL}/api/prestations/${id}`,
  PRESTATION_DAYS: (id) => `${API_BASE_URL}/api/prestations/${id}/days`,
  PRESTATION_DELETE_DAY: (id, dayId) => `${API_BASE_URL}/api/prestations/${id}/days/${dayId}`,
  PRESTATION_DELETE_DAY_BY_DATE: (id, date) => `${API_BASE_URL}/api/prestations/${id}/days/by-date/${date}`,
  PRESTATION_DAY_PARTICIPANTS: (dayId) => `${API_BASE_URL}/api/prestations/days/${dayId}/participants`,
  PRESTATION_STATUSES: `${API_BASE_URL}/api/prestations/statuses`,
  PRESTATION_CHECK_CONFLICTS: `${API_BASE_URL}/api/prestations/check-participant-conflicts`,
  PRESTATION_RESOLVE_CONFLICTS: `${API_BASE_URL}/api/prestations/resolve-conflicts`,
  PRESTATION_FIND_REPLACEMENTS: `${API_BASE_URL}/api/prestations/find-replacements`,
  PRESTATION_REPLACE_PARTICIPANT: `${API_BASE_URL}/api/prestations/replace-participant`,
  PRESTATION_MOVE: (id) => `${API_BASE_URL}/api/prestations/${id}/move`,
  PRESTATION_STATUS: (id) => `${API_BASE_URL}/api/prestations/${id}/status`,
  PRESTATION_LOCK: (id) => `${API_BASE_URL}/api/prestations/${id}/lock`,
  PRESTATION_UNLOCK: (id) => `${API_BASE_URL}/api/prestations/${id}/unlock`,
  PRESTATION_TOGGLE_BLOCK: (id) => `${API_BASE_URL}/api/prestations/${id}/toggle-block`,
  PRESTATION_LOAD_GROUP_MEMBERS: (id) => `${API_BASE_URL}/api/prestations/${id}/load-group-members`,
  PRESTATION_CALENDAR: (year, month) => `${API_BASE_URL}/api/prestations/calendar/${year}/${month}`,
  PRESTATION_SNAPSHOTS: (id) => `${API_BASE_URL}/api/prestations/${id}/snapshots`,
  PRESTATION_ALTERNATIVES: `${API_BASE_URL}/api/prestations/alternatives`,
  PRESTATION_RESOLVE_CONFLICT: `${API_BASE_URL}/api/prestations/resolve-conflict`,
  PRESTATION_SEARCH: `${API_BASE_URL}/api/prestations/search`,
  GROUP_ALTERNATIVES: `${API_BASE_URL}/api/groups/alternatives`,
  USER_TEAM_LEADER_ALTERNATIVES: `${API_BASE_URL}/api/users/team-leader-alternatives`,

  // Groupes
  GROUPS: `${API_BASE_URL}/api/groups`,
  GROUP_BY_ID: (id) => `${API_BASE_URL}/api/groups/${id}`,
  GROUP_MEMBERS: (id) => `${API_BASE_URL}/api/groups/${id}/members`,
  GROUP_TEAM_LEADER: (id) => `${API_BASE_URL}/api/groups/${id}/team-leader`,
  GROUP_AVAILABILITY: (date) => `${API_BASE_URL}/api/groups/availability/${date}`,

  // Clients
  CLIENTS: `${API_BASE_URL}/api/clients`,
  CLIENTS_ALL: `${API_BASE_URL}/api/clients/all`,
  CLIENT_BY_ID: (id) => `${API_BASE_URL}/api/clients/${id}`,
  CLIENTS_SEARCH: `${API_BASE_URL}/api/clients/search`,
  CLIENTS_CITIES: `${API_BASE_URL}/api/clients/cities`,
  CLIENTS_STATS: `${API_BASE_URL}/api/clients/stats`,
  CLIENTS_IMPORT: `${API_BASE_URL}/api/clients/import`,
  CLIENTS_DISTANCE_STATS: `${API_BASE_URL}/api/clients/distance-stats`,
  CLIENTS_DISTANCE_BATCH: `${API_BASE_URL}/api/clients/calculate-distance-batch`,
  CLIENT_LOCATIONS: (id) => `${API_BASE_URL}/api/clients/${id}/locations`,
  CLIENTS_TYPES_STATS: `${API_BASE_URL}/api/clients/types/stats`,
  CLIENTS_AGENCIES: `${API_BASE_URL}/api/clients/agencies`,
  CLIENTS_BULK_QUALIFY: `${API_BASE_URL}/api/clients/bulk-qualify`,

  // Lieux de mise (locations)
  LOCATIONS: `${API_BASE_URL}/api/locations`,
  LOCATIONS_CITIES: `${API_BASE_URL}/api/locations/cities`,
  LOCATION_BY_ID: (id) => `${API_BASE_URL}/api/locations/${id}`,
  LOCATIONS_BY_CLIENT: (clientId) => `${API_BASE_URL}/api/locations/by-client/${clientId}`,
  LOCATION_CLIENTS: (id) => `${API_BASE_URL}/api/locations/${id}/clients`,
  LOCATION_REMOVE_CLIENT: (id, clientId) => `${API_BASE_URL}/api/locations/${id}/clients/${clientId}`,
  LOCATIONS_IMPORT: `${API_BASE_URL}/api/locations/import`,
  LOCATIONS_DISTANCE_STATS: `${API_BASE_URL}/api/locations/distance-stats`,
  LOCATIONS_DISTANCE_BATCH: `${API_BASE_URL}/api/locations/calculate-distance-batch`,

  // Days management
  PRESTATION_DAYS_PARTICIPANTS: (dayId) => `${API_BASE_URL}/api/prestations/days/${dayId}/participants`,
  PRESTATION_DAY_TEAM_LEADER: (dayId) => `${API_BASE_URL}/api/prestations/days/${dayId}/team-leader`,

  // Utilisateurs
  USERS: `${API_BASE_URL}/api/users`,
  USER_BY_ID: (id) => `${API_BASE_URL}/api/users/${id}`,
  USERS_LOOKUP_ROLES: `${API_BASE_URL}/api/users/lookup/roles`,
  USERS_LOOKUP_TEAMS: `${API_BASE_URL}/api/users/lookup/teams`,
  USERS_LOOKUP_EMPLOYEE_STATUSES: `${API_BASE_URL}/api/users/lookup/employee-statuses`,
  USER_AVAILABILITY: `${API_BASE_URL}/api/users/availability`,
  USERS_FIND_REPLACEMENTS: `${API_BASE_URL}/api/users/find-replacements`,
  USERS_BULK_AVAILABILITY: `${API_BASE_URL}/api/users/bulk-availability`,
  USERS_TEAM_LEADERS: `${API_BASE_URL}/api/users?role=Chef d'equipe`,
  USERS_PENDING_REVIEW: `${API_BASE_URL}/api/users/pending-review`,
  USERS_SEARCH_INTERIMS: `${API_BASE_URL}/api/users/search/interims`,
  USER_VALIDATE: (id) => `${API_BASE_URL}/api/users/${id}/validate`,

  // Planning RH
  HR_MONTHLY_PLANNING: (start, end) => `${API_BASE_URL}/api/hr/monthly-planning/${start}/${end}`,
  HR_USER_AVAILABILITY: `${API_BASE_URL}/api/hr/user-availability`,
  HR_USER_AVAILABILITY_DELETE: (userId, date) => `${API_BASE_URL}/api/hr/user-availability/${userId}/${date}`,
  HR_LEAVE_SUMMARY: `${API_BASE_URL}/api/hr/leave-summary`,
  HR_USER_LEAVE_SUMMARY: (userId) => `${API_BASE_URL}/api/hr/user-leave-summary/${userId}`,
  HR_AVAILABILITY_TYPES: `${API_BASE_URL}/api/hr/availability-types`,
  HR_ADD_TO_TRAINING: `${API_BASE_URL}/api/hr/add-to-training`,
  HR_QUALITY_CONTROL: (startDate, endDate) => `${API_BASE_URL}/api/hr/quality-control/${startDate}/${endDate}`,
  HR_EXPORT_USER_HOURS: (userId, startDate, endDate) => `${API_BASE_URL}/api/hr/export-user-hours/${userId}/${startDate}/${endDate}`,
  HR_LEAVE_REPORT: (startDate, endDate) => `${API_BASE_URL}/api/hr/leave-report/${startDate}/${endDate}`,
  HR_USER_LEAVE: (userId, startDate, endDate) => `${API_BASE_URL}/api/users/${userId}/leave/${startDate}/${endDate}`,
  HR_RESOLVE_CONFLICT: `${API_BASE_URL}/api/hr/resolve-hr-planning-conflict`,
  HR_PLANNING_CONFLICTS: (startDate, endDate) => `${API_BASE_URL}/api/hr/planning-conflicts?start_date=${startDate}&end_date=${endDate}`,
  HR_INTERIM_WORKERS: (startDate, endDate) => `${API_BASE_URL}/api/hr/interim-workers/${startDate}/${endDate}`,
  HR_INTERIM_EXPORT: (agencyId, startDate, endDate) => `${API_BASE_URL}/api/hr/interim-export/${agencyId}/${startDate}/${endDate}`,
  HR_INTERIM_EXPORT_PDF: (agencyId, startDate, endDate) => `${API_BASE_URL}/api/hr/interim-export-pdf/${agencyId}/${startDate}/${endDate}`,
  HR_ACTIVE_GROUPS: (date) => `${API_BASE_URL}/api/hr/active-groups/${date}`,
  HR_QUICK_REMOVE_FROM_PRESTATION: `${API_BASE_URL}/api/hr/quick-remove-from-prestation`,
  HR_QUICK_ADD_TO_GROUP: `${API_BASE_URL}/api/hr/quick-add-to-group`,
  HR_AUTO_FILL_NON_TERRAIN: (start, end) => `${API_BASE_URL}/api/hr/auto-fill-non-terrain/${start}/${end}`,
  HR_DAILY_WORKFORCE_PDF: (date) => `${API_BASE_URL}/api/hr/daily-workforce-pdf/${date}`,
  HR_NEXT_ACTIVE_DAY: (from) => `${API_BASE_URL}/api/hr/next-active-day?from=${from}`,
  HR_COUNTERS_PDF: `${API_BASE_URL}/api/hr/counters-pdf`,
  HR_COUNTERS_EXCEL: `${API_BASE_URL}/api/hr/counters-excel`,
  HR_COUNTERS_WEEKLY_PDF: (start, end) => `${API_BASE_URL}/api/hr/counters-weekly-pdf?start=${start}&end=${end}`,
  HR_COUNTERS_WEEKLY_EXCEL: (start, end) => `${API_BASE_URL}/api/hr/counters-weekly-excel?start=${start}&end=${end}`,

  // Feuilles journalières de travail
  WORKSHEETS: `${API_BASE_URL}/api/worksheets`,
  WORKSHEET_BY_ID: (id) => `${API_BASE_URL}/api/worksheets/${id}`,
  WORKSHEET_STATUSES: `${API_BASE_URL}/api/worksheets/statuses`,
  WORKSHEET_BY_PRESTATION: (prestationId) => `${API_BASE_URL}/api/worksheets/prestation/${prestationId}`,
  WORKSHEET_BY_PRESTATION_DAY: (prestationId, date) => `${API_BASE_URL}/api/worksheets/prestation/${prestationId}/day/${date}`,
  WORKSHEET_SUBMIT: (id) => `${API_BASE_URL}/api/worksheets/${id}/submit`,
  WORKSHEET_VALIDATE: (id) => `${API_BASE_URL}/api/worksheets/${id}/validate`,
  WORKSHEET_CHECK: (id) => `${API_BASE_URL}/api/worksheets/${id}/check`,
  WORKSHEET_INVALIDATE: (id) => `${API_BASE_URL}/api/worksheets/${id}/invalidate`,
  WORKSHEET_REJECT: (id) => `${API_BASE_URL}/api/worksheets/${id}/reject`,
  WORKSHEET_CALCULATE_HOURS: `${API_BASE_URL}/api/worksheets/calculate-hours`,
  WORKSHEET_STATS_PENDING: `${API_BASE_URL}/api/worksheets/stats/pending`,
  WORKSHEET_PDF: (id) => `${API_BASE_URL}/api/worksheets/${id}/pdf`,

  // Véhicules (fourgons)
  VEHICLES: `${API_BASE_URL}/api/vehicles`,

  // Jours feries
  HOLIDAYS: `${API_BASE_URL}/api/holidays`,
  HOLIDAYS_BY_YEAR: (year) => `${API_BASE_URL}/api/holidays?year=${year}`,
  HOLIDAY_BY_ID: (id) => `${API_BASE_URL}/api/holidays/${id}`,
  HOLIDAYS_RANGE: (start, end) => `${API_BASE_URL}/api/holidays/range/${start}/${end}`,
  HOLIDAYS_CREATE_RANGE: `${API_BASE_URL}/api/holidays/range`,
  HOLIDAYS_CHECK: (date) => `${API_BASE_URL}/api/holidays/check/${date}`,
  HOLIDAYS_GENERATE: (year) => `${API_BASE_URL}/api/holidays/generate/${year}`,

  // Planning/Drag mode
  PLANNING_HEALTH: `${API_BASE_URL}/api/health`,
  VERSION: `${API_BASE_URL}/api/version`,       // Public - version check
  VERSIONS: `${API_BASE_URL}/api/versions`,     // Admin - infos complètes
  PLANNING_DRAG_MODE_STATUS: `${API_BASE_URL}/api/planning/drag-mode-status`,
  PLANNING_DRAG_MODE_ACQUIRE: `${API_BASE_URL}/api/planning/drag-mode/acquire`,
  PLANNING_DRAG_MODE_RELEASE: `${API_BASE_URL}/api/planning/drag-mode/release`,

  // Authentification
  AUTH: `${API_BASE_URL}/api/auth`,
  AUTH_LOGIN: `${API_BASE_URL}/api/auth/login`,
  AUTH_REFRESH: `${API_BASE_URL}/api/auth/refresh`,
  AUTH_LOGOUT: `${API_BASE_URL}/api/auth/logout`,
  AUTH_FORGOT_PASSWORD: `${API_BASE_URL}/api/auth/forgot-password`,
  AUTH_RESET_PASSWORD: `${API_BASE_URL}/api/auth/reset-password`,

  // WebSocket
  WEBSOCKET_URL: getEnvVar('REACT_APP_SOCKET_URL', 'http://localhost:3001'),

  // Admin
  ADMIN_ROLES: `${API_BASE_URL}/api/admin/roles`,
  ADMIN_ROLE_BY_ID: (id) => `${API_BASE_URL}/api/admin/roles/${id}`,
  ADMIN_PERMISSIONS: `${API_BASE_URL}/api/admin/permissions`,
  ADMIN_CONFIG: `${API_BASE_URL}/api/admin/config`,
  ADMIN_CONFIG_BY_KEY: (key) => `${API_BASE_URL}/api/admin/config/${key}`,

  // Analytics & Tracking
  ANALYTICS_TRACK: `${API_BASE_URL}/api/analytics/track`,
  ANALYTICS_TRACK_BATCH: `${API_BASE_URL}/api/analytics/track/batch`,
  ANALYTICS_EVENTS: `${API_BASE_URL}/api/analytics/events`,
  ANALYTICS_STATS: `${API_BASE_URL}/api/analytics/stats`,
  ANALYTICS_AUDIT: (entityType, entityId) => `${API_BASE_URL}/api/analytics/audit/${entityType}/${entityId}`,

  // Performance (Phase 3)
  PERF_METRICS: `${API_BASE_URL}/api/perf/metrics`,
  PERF_SLOW_REQUESTS: `${API_BASE_URL}/api/perf/slow-requests`,
  PERF_ERRORS: `${API_BASE_URL}/api/perf/errors`,
  PERF_RECENT: `${API_BASE_URL}/api/perf/recent`,
  PERF_REPORT: `${API_BASE_URL}/api/perf/report`,
  PERF_RESET: `${API_BASE_URL}/api/perf/reset`,
  PERF_CLIENT_ERROR: `${API_BASE_URL}/api/perf/client-error`,

  // OCR (Backend unifie - PaddleOCR + Tesseract fallback)
  OCR_HEALTH: `${API_BASE_URL}/api/ocr/health`,
  OCR_DIAGNOSTIC: `${API_BASE_URL}/api/ocr/diagnostic`,
  OCR_PADDLE: `${API_BASE_URL}/api/ocr/paddle`,
  OCR_PADDLE_EXTRACT: `${API_BASE_URL}/api/ocr/paddle/extract`,
  OCR_TESSERACT: `${API_BASE_URL}/api/ocr/tesseract`,
  OCR_ANALYZE: `${API_BASE_URL}/api/ocr/analyze`,
  OCR_STATS: `${API_BASE_URL}/api/ocr/stats`,
  OCR_SURYA_HEALTH: `${API_BASE_URL}/api/ocr/surya/health`,
  OCR_SURYA: `${API_BASE_URL}/api/ocr/surya`,
  OCR_SURYA_EXTRACT: `${API_BASE_URL}/api/ocr/surya/extract`,
  OCR_COMPARE: `${API_BASE_URL}/api/ocr/compare`,
  // OCR v2 (DocTR + OpenCV preprocessing)
  OCR_V2_HEALTH: `${API_BASE_URL}/api/ocr/v2/health`,
  OCR_V2: `${API_BASE_URL}/api/ocr/v2`,
  // OCR AWS Textract
  OCR_TEXTRACT_HEALTH: `${API_BASE_URL}/api/ocr/textract/health`,
  OCR_TEXTRACT: `${API_BASE_URL}/api/ocr/textract/extract`,
  // OCR Google Cloud Vision
  OCR_GOOGLE_VISION_HEALTH: `${API_BASE_URL}/api/ocr/google-vision/health`,
  OCR_GOOGLE_VISION: `${API_BASE_URL}/api/ocr/google-vision/extract`,
  // OCR EasyOCR
  OCR_EASYOCR_HEALTH: `${API_BASE_URL}/api/ocr/easyocr/health`,
  OCR_EASYOCR_EXTRACT: `${API_BASE_URL}/api/ocr/easyocr/extract`,
  // OCR HuggingFace Inference API
  OCR_HF_HEALTH: `${API_BASE_URL}/api/ocr/hf/health`,
  OCR_HF_EXTRACT: `${API_BASE_URL}/api/ocr/hf/extract`,

  // Geo (IGN aerial photos)
  GEO: `${API_BASE_URL}/api/geo`,
  GEO_AERIAL_PHOTO: `${API_BASE_URL}/api/geo/aerial-photo`,

  // Presence (Phase 2)
  PRESENCE_ACTIVE_USERS: `${API_BASE_URL}/api/presence/active`,
  PRESENCE_STATS: `${API_BASE_URL}/api/presence/stats`,

  // Analytics Dashboard (Phase 4)
  ANALYTICS_DASHBOARD_OVERVIEW: `${API_BASE_URL}/api/analytics/dashboard/overview`,
  ANALYTICS_DASHBOARD_EVENTS: `${API_BASE_URL}/api/analytics/dashboard/events`,
  ANALYTICS_DASHBOARD_AUDIT: `${API_BASE_URL}/api/analytics/dashboard/audit`,
  ANALYTICS_DASHBOARD_USERS: `${API_BASE_URL}/api/analytics/dashboard/users`,
  ANALYTICS_DASHBOARD_EXPORT: `${API_BASE_URL}/api/analytics/dashboard/export`,
  // Cloud Services Usage Analytics
  ANALYTICS_CLOUD_USAGE: `${API_BASE_URL}/api/analytics/dashboard/cloud-usage`,
  ANALYTICS_CLOUD_USAGE_BUDGET: `${API_BASE_URL}/api/analytics/dashboard/cloud-usage/budget`,
  ANALYTICS_CLOUD_USAGE_RECENT: `${API_BASE_URL}/api/analytics/dashboard/cloud-usage/recent`,

  // Dashboard Widgets
  TEAM_CALENDAR: (start, end) => `${API_BASE_URL}/api/dashboard/team-calendar/${start}/${end}`,

  // Demandes de congés
  LEAVE_REQUESTS: `${API_BASE_URL}/api/leave-requests`,
  LEAVE_REQUESTS_MY: `${API_BASE_URL}/api/leave-requests/my`,
  LEAVE_REQUESTS_PENDING: `${API_BASE_URL}/api/leave-requests/pending`,
  LEAVE_REQUESTS_STATS: `${API_BASE_URL}/api/leave-requests/stats`,
  LEAVE_REQUEST_BY_ID: (id) => `${API_BASE_URL}/api/leave-requests/${id}`,
  LEAVE_REQUESTS_TYPES: `${API_BASE_URL}/api/leave-requests/types`,
  LEAVE_REQUESTS_STATUSES: `${API_BASE_URL}/api/leave-requests/statuses`,
  LEAVE_REQUESTS_CHECK_AVAILABILITY: `${API_BASE_URL}/api/leave-requests/check-availability`,
  LEAVE_REQUEST_SUBMIT: (id) => `${API_BASE_URL}/api/leave-requests/${id}/submit`,
  LEAVE_REQUEST_APPROVE: (id) => `${API_BASE_URL}/api/leave-requests/${id}/approve`,
  LEAVE_REQUEST_REJECT: (id) => `${API_BASE_URL}/api/leave-requests/${id}/reject`,
  LEAVE_REQUEST_CANCEL: (id) => `${API_BASE_URL}/api/leave-requests/${id}/cancel`,

  // Notes de frais (Expenses)
  EXPENSES: `${API_BASE_URL}/api/expenses`,
  EXPENSES_MY: `${API_BASE_URL}/api/expenses/my`,
  EXPENSES_PENDING: `${API_BASE_URL}/api/expenses/pending`,
  EXPENSES_VALIDATED: `${API_BASE_URL}/api/expenses/validated`,
  EXPENSES_VALIDATION_COUNTS: `${API_BASE_URL}/api/expenses/validation-counts`,
  EXPENSES_STATS: `${API_BASE_URL}/api/expenses/stats/summary`,
  EXPENSES_DASHBOARD: `${API_BASE_URL}/api/expenses/stats/dashboard`,
  EXPENSES_EXPORT: `${API_BASE_URL}/api/expenses/export`,
  EXPENSES_ALL: `${API_BASE_URL}/api/expenses/all`,
  EXPENSE_BY_ID: (id) => `${API_BASE_URL}/api/expenses/${id}`,
  EXPENSES_TYPES: `${API_BASE_URL}/api/expenses/types`,
  EXPENSES_STATUSES: `${API_BASE_URL}/api/expenses/statuses`,
  EXPENSE_SUBMIT: (id) => `${API_BASE_URL}/api/expenses/${id}/submit`,
  EXPENSE_CANCEL: (id) => `${API_BASE_URL}/api/expenses/${id}/cancel`,
  EXPENSE_VALIDATE: (id) => `${API_BASE_URL}/api/expenses/${id}/validate`,
  EXPENSE_REJECT: (id) => `${API_BASE_URL}/api/expenses/${id}/reject`,
  EXPENSE_RETURN_FOR_CORRECTION: (id) => `${API_BASE_URL}/api/expenses/${id}/return-for-correction`,
  EXPENSE_MARK_PAID: (id) => `${API_BASE_URL}/api/expenses/${id}/mark-paid`,
  EXPENSE_BENEFICIARIES: (id) => `${API_BASE_URL}/api/expenses/${id}/beneficiaries`,
  EXPENSE_RECEIPT: (id) => `${API_BASE_URL}/api/expenses/${id}/receipt`,
  EXPENSES_USERS_FOR_BENEFICIARY: `${API_BASE_URL}/api/expenses/users-for-beneficiary`,
  // Frais hebdomadaires
  EXPENSES_WEEKLY: `${API_BASE_URL}/api/expenses/weekly`,
  EXPENSES_WEEKLY_USER: (userId) => `${API_BASE_URL}/api/expenses/weekly/${userId}`,
  EXPENSES_WEEKLY_PDF_INDIVIDUAL: (userId) => `${API_BASE_URL}/api/expenses/weekly/pdf/individual/${userId}`,
  EXPENSES_WEEKLY_PDF_GROUP: `${API_BASE_URL}/api/expenses/weekly/pdf/group`,
  EXPENSES_WEEKLY_GAMELLE_MARK_PAID: `${API_BASE_URL}/api/expenses/weekly/gamelle/mark-paid`,
  // Dettes/Régularisations
  EXPENSES_DEBTS: `${API_BASE_URL}/api/expenses/debts`,
  EXPENSES_DEBTS_MY: `${API_BASE_URL}/api/expenses/debts/my`,
  EXPENSE_DEBT_BY_ID: (id) => `${API_BASE_URL}/api/expenses/debts/${id}`,
  EXPENSE_DEBT_CANCEL: (id) => `${API_BASE_URL}/api/expenses/debts/${id}/cancel`,

  // Feedback
  FEEDBACK: `${API_BASE_URL}/api/feedback`,
  FEEDBACK_MY: `${API_BASE_URL}/api/feedback/my`,
  FEEDBACK_MODULES: `${API_BASE_URL}/api/feedback/modules`,
  FEEDBACK_BY_ID: (id) => `${API_BASE_URL}/api/feedback/${id}`,
  FEEDBACK_SUBMIT: (id) => `${API_BASE_URL}/api/feedback/${id}/submit`,
  FEEDBACK_REVIEW: (id) => `${API_BASE_URL}/api/feedback/${id}/review`,
  FEEDBACK_ADMIN_ALL: `${API_BASE_URL}/api/feedback/admin/all`,
  FEEDBACK_STATS: `${API_BASE_URL}/api/feedback/stats`,
  FEEDBACK_EXPORT: `${API_BASE_URL}/api/feedback/export`,

  // Batch API (optimisation N+1 queries)
  BATCH_LOOKUPS: `${API_BASE_URL}/api/batch/lookups`,
  BATCH_SESSION_FORM: `${API_BASE_URL}/api/batch/session-form`,
  BATCH_HR_PLANNING: (startDate, endDate) => `${API_BASE_URL}/api/batch/hr-planning?start_date=${startDate}&end_date=${endDate}`,
  BATCH_EXPENSE_FORM: `${API_BASE_URL}/api/batch/expense-form`,

  // Work Sheets (fiches de chantier)
  WORK_SHEETS: `${API_BASE_URL}/api/work-sheets`,
  WORK_SHEET_BY_ID: (id) => `${API_BASE_URL}/api/work-sheets/${id}`,
  WORK_SHEETS_BY_ORDER: (orderNumber) => `${API_BASE_URL}/api/work-sheets/by-order/${encodeURIComponent(orderNumber)}`,
  WORK_SHEET_PREVIEW: (id) => `${API_BASE_URL}/api/work-sheets/${id}/preview`,
  WORK_SHEET_DOWNLOAD: (id) => `${API_BASE_URL}/api/work-sheets/${id}/download`
};

// Helpers pour construction URLs dynamiques
export const buildApiUrl = (endpoint, params = {}) => {
  let url = endpoint;

  // Remplacer les parametres dans l'URL
  Object.keys(params).forEach(key => {
    url = url.replace(`:${key}`, params[key]);
  });

  return url;
};

// Fonction pour ajouter query parameters
export const addQueryParams = (url, params = {}) => {
  const urlObj = new URL(url);

  Object.keys(params).forEach(key => {
    if (params[key] !== null && params[key] !== undefined) {
      urlObj.searchParams.append(key, params[key]);
    }
  });

  return urlObj.toString();
};

// Headers par defaut pour les requetes
export const getDefaultHeaders = (token = null) => {
  const headers = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

/**
 * Intercepte et stocke un nouveau token si le serveur en renvoie un
 * Le header X-New-Token est envoyé quand le token actuel expire bientôt
 */
export const handleTokenRefresh = (response) => {
  const newToken = response.headers.get('X-New-Token');
  if (newToken) {
    localStorage.setItem('token', newToken);
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Token automatiquement renouvelé');
    }
    return true;
  }
  return false;
};

/**
 * Gère les erreurs 401 (token expiré/invalide) de manière globale
 * Émet un événement personnalisé pour déclencher la déconnexion
 *
 * NOTE: Cette fonction est maintenant principalement gérée par fetchInterceptor.js
 * qui intercepte TOUS les appels fetch() de l'application.
 * Cette fonction reste disponible pour les cas où on utilise apiRequest directement.
 */
export const handleUnauthorized = (response) => {
  if (response.status === 401) {
    // L'intercepteur global gère déjà ça, mais on garde cette fonction
    // pour la rétro-compatibilité avec les appels directs à apiRequest
    console.info('Session expirée (401) - géré par fetchInterceptor');
    return true;
  }
  return false;
};

/**
 * Fetch wrapper avec gestion automatique du refresh token et des erreurs 401
 * Utiliser à la place de fetch() pour bénéficier du renouvellement automatique
 * et de la déconnexion automatique en cas de token expiré
 *
 * @example
 * const response = await apiFetch('/api/users', {
 *   headers: { 'Authorization': `Bearer ${token}` }
 * });
 */
export const apiFetch = async (url, options = {}) => {
  const response = await fetch(url, options);
  // Vérifier d'abord si c'est un 401 (token expiré)
  if (handleUnauthorized(response)) {
    // Retourner la réponse pour que le composant puisse gérer l'erreur
    return response;
  }
  // Sinon, vérifier si un nouveau token a été envoyé
  handleTokenRefresh(response);
  return response;
};

// Fonction utilitaire pour requetes API standardisees
export const apiRequest = async (endpoint, options = {}) => {
  const { token, method = 'GET', body, queryParams } = options;

  let url = endpoint;
  if (queryParams) {
    url = addQueryParams(url, queryParams);
  }

  const config = {
    method,
    headers: getDefaultHeaders(token),
    ...options
  };

  if (body && method !== 'GET') {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, config);

    // Vérifier d'abord si c'est un 401 (token expiré)
    if (handleUnauthorized(response)) {
      throw new Error('Session expirée');
    }

    // Intercepter le refresh token automatique
    handleTokenRefresh(response);

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}`);
    }

    return data;
  } catch (error) {
    // En production, on ne log pas les erreurs API en console
    if (process.env.NODE_ENV === 'development') {
      console.error(`API Error (${method} ${url}):`, error);
    }
    throw error;
  }
};

// Export de la configuration pour compatibilite avec constants.js
export default API_ENDPOINTS;
